/**
 * Shell: layout only. Bar, FAB, slides wrap; drawer is in shell-drawer.
 * @see docs/architecture/GREENFIELD-LECTURA-ARCHITECTURE.md
 */

import { createEl } from '../core/dom';

export { createDrawerDOM, mountDrawer, type CreateDrawerDOMOpts } from './shell-drawer';
export { createDrawerView } from './drawer-view';

export interface BarResult {
  bar: HTMLElement;
  slots: {
    tts: HTMLElement;
    chat: HTMLElement;
    profile: HTMLElement;
    remote: HTMLElement;
    slideNumber: HTMLElement;
    presence: HTMLElement;
  };
  barSlideNumTextEl: HTMLElement | null;
}

/**
 * Create bar with slots (tts, chat, remote, profile, slideNumber, presence).
 */
export function createBar(parent?: HTMLElement | null): BarResult {
  const bar = createEl('div', { className: 'lectura-bar', role: 'toolbar', 'aria-label': 'Lectura: sound, chat, profile' });
  const ttsSlot = createEl('div', { className: 'lectura-bar-tts' });
  const chatSlot = createEl('div', { className: 'lectura-bar-chat' });
  const profileSlot = createEl('div', { className: 'lectura-bar-profile' });
  const remoteSlot = createEl('div', { className: 'lectura-bar-remote' });
  const slideNumSlot = createEl('div', { className: 'lectura-bar-slide-number' });
  const presenceSpan = createEl('span', { className: 'rf-presence', 'aria-label': 'Viewers' });

  bar.appendChild(ttsSlot);
  bar.appendChild(chatSlot);
  bar.appendChild(remoteSlot);
  bar.appendChild(profileSlot);
  bar.appendChild(slideNumSlot);

  let barSlideNumTextEl: HTMLElement | null = null;
  const revealSlideNumber = parent ? parent.querySelector('.slide-number') : null;
  if (revealSlideNumber && revealSlideNumber.parentNode) {
    slideNumSlot.appendChild(revealSlideNumber);
  } else {
    barSlideNumTextEl = createEl('span', { className: 'rf-bar-slide-num' });
    barSlideNumTextEl.textContent = '1 / 1';
    slideNumSlot.appendChild(barSlideNumTextEl);
  }
  slideNumSlot.appendChild(presenceSpan);

  if (parent) parent.appendChild(bar);
  return { bar, slots: { tts: ttsSlot, chat: chatSlot, profile: profileSlot, remote: remoteSlot, slideNumber: slideNumSlot, presence: presenceSpan }, barSlideNumTextEl };
}

export interface RenderFabOpts {
  fabLabel?: string;
  container?: HTMLElement;
  title?: string;
}

/**
 * Render FAB (and optional wrap). Click calls onToggle.
 */
export function renderFab(
  onToggle: (e: MouseEvent) => void,
  opts: RenderFabOpts = {}
): { fab: HTMLElement; badge: HTMLElement } | null {
  const label = opts.fabLabel || 'Chat';
  const title = opts.title || (label === 'Chat' ? 'Chat (C)' : `${label} (C)`);
  const fab = createEl('button', {
    type: 'button',
    className: 'rf-fab',
    'aria-label': label,
    title,
    html: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>'
  });
  const badge = createEl('span', { className: 'rf-fab-badge rf-hidden', 'aria-hidden': 'true' });
  fab.appendChild(badge);
  fab.addEventListener('click', onToggle);
  const wrap = createEl('div', { className: 'rf-fab-wrap' });
  wrap.appendChild(fab);
  (opts.container || document.body).appendChild(wrap);
  return { fab, badge };
}

export function ensureSlidesWrap(): HTMLElement {
  const wrapExisting = document.querySelector('.rf-slides-chat-wrap');
  if (wrapExisting) return wrapExisting as HTMLElement;
  const reveal = document.querySelector('.reveal');
  if (!reveal?.parentNode) return document.body;
  const wrap = createEl('div', { className: 'rf-slides-chat-wrap' });
  reveal.parentNode.insertBefore(wrap, reveal);
  wrap.appendChild(reveal);
  return wrap;
}
